import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';
import HikReadOnly from '../components/HikReadOnly';

const STATUS = {
  pending: { text: '待执行', bg: '#fff7e6', color: '#d46b08' },
  picked: { text: '执行中', bg: '#e6f4ff', color: '#1677ff' },
  success: { text: '已成功', bg: '#f6ffed', color: '#389e0d' },
  failed: { text: '失败', bg: '#fff1f0', color: '#cf1322' },
  synced: { text: '已同步', bg: '#f6ffed', color: '#389e0d' },
  registered: { text: '已同步', bg: '#f6ffed', color: '#389e0d' },
  active: { text: '已同步', bg: '#f6ffed', color: '#389e0d' },
  unsynced: { text: '未同步', bg: '#f5f5f5', color: '#666' }
};

function arr(data, key) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data[key])) return data[key];
  return [];
}

function fmtTime(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString('zh-CN', { hour12: false });
}

function imageUrl(v) {
  if (!v) return '';
  if (/^https?:\/\//i.test(v) || v.startsWith('/')) return v;
  return `/uploads/${v}`;
}

function badge(status) {
  const key = String(status || 'unsynced').toLowerCase();
  return STATUS[key] || { text: status || '未同步', bg: '#f5f5f5', color: '#666' };
}

export default function FaceManage() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState({});
  const [people, setPeople] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [keyword, setKeyword] = useState('');
  const [error, setError] = useState('');

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const [o, p, t] = await Promise.all([
        API.get('/access-admin/overview'),
        API.get('/access-admin/people'),
        API.get('/access-admin/tasks')
      ]);
      setOverview(o.data || {});
      setPeople(arr(p.data, 'people'));
      setTasks(arr(t.data, 'tasks'));
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data?.error || e.message || '读取门禁数据失败';
      setError(msg);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const timer = setInterval(() => load(true), 15000);
    return () => clearInterval(timer);
  }, []);

  const gateway =
    overview.gateway ||
    overview.latest_gateway ||
    (Array.isArray(overview.gateways) ? overview.gateways[0] : null) ||
    {};

  const settings = overview.settings || overview.config || {};
  const counts = overview.counts || overview.task_counts || {};

  const lastSeen = gateway.last_seen || overview.last_seen;
  const secondsSinceSeen = lastSeen ? (Date.now() - new Date(lastSeen).getTime()) / 1000 : 999999;
  const online =
    typeof overview.online === 'boolean'
      ? overview.online
      : typeof gateway.online === 'boolean'
        ? gateway.online
        : secondsSinceSeen < 60;

  const runMode = settings.run_mode || overview.run_mode || '—';
  const writeEnabledRaw =
    settings.write_enabled !== undefined ? settings.write_enabled : overview.write_enabled;
  const writeEnabled = writeEnabledRaw === true || Number(writeEnabledRaw) === 1;
  const testRoom = settings.test_room_no || overview.test_room_no || '';

  const deviceWriteEnabled =
    gateway.device_write_enabled === true ||
    Number(gateway.device_write_enabled) === 1;

  const taskPollingEnabled =
    gateway.task_polling_enabled === true ||
    Number(gateway.task_polling_enabled) === 1;

  const pending =
    counts.pending ??
    overview.pending ??
    overview.pending_tasks ??
    tasks.filter(x => String(x.status).toLowerCase() === 'pending').length;

  const filtered = useMemo(() => {
    const k = keyword.trim().toLowerCase();
    if (!k) return people;
    return people.filter(p =>
      [p.real_name, p.room_no, p.phone, p.person_uuid]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(k))
    );
  }, [people, keyword]);

  const syncPerson = async (p) => {
    const userId = p.user_id || p.id;
    if (!userId) {
      alert('这个人员没有可用的用户ID，暂时不能下发');
      return;
    }
    if (!window.confirm(`确定把 ${p.real_name || '该住户'}（${p.room_no || '无房号'}）下发到安居宝门禁吗？`)) return;

    setBusyId(userId);
    try {
      const r = await API.post(`/access-admin/sync/${userId}`);
      alert(r.data?.message || '已加入门禁下发队列');
      await load(true);
    } catch (e) {
      alert(e.response?.data?.message || e.response?.data?.error || e.message || '下发失败');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button style={s.back} onClick={() => navigate('/')}>← 返回</button>
        <div>
          <div style={s.title}>统一门禁管理</div>
          <div style={s.subTitle}>瓯江印象设备网关 · 物业管理中心</div>
        </div>
        <button style={s.refresh} onClick={() => load()}>刷新</button>
      </div>

      <main style={s.body}>
        <HikReadOnly />
        {error && <div style={s.error}>⚠️ {error}</div>}

        <section style={s.card}>
          <div style={s.sectionHead}>
            <b>网关状态</b>
            <span style={{...s.onlineBadge, ...(online ? s.online : s.offline)}}>
              {online ? '● 在线' : '● 离线'}
            </span>
          </div>

          <div style={s.gatewayName}>
            {gateway.gateway_name || gateway.gateway_id || '现场设备网关'}
          </div>

          <div style={s.grid}>
            <Info label="最后心跳" value={fmtTime(lastSeen)} />
            <Info label="版本" value={gateway.version || '—'} />
            <Info label="本地IP" value={gateway.local_ip || '—'} />
            <Info label="待执行" value={`${pending || 0} 条`} />
          </div>

          <div style={s.modeRow}>
            <span>运行模式：<b>{runMode === 'test' ? '测试模式' : runMode}</b></span>
            <span>云端写入授权：
              <b style={{color: writeEnabled ? '#389e0d' : '#cf1322'}}>
                {writeEnabled ? '已开启' : '未开启'}
              </b>
            </span>
          </div>

          <div style={s.modeRow}>
            <span>现场设备写入：
              <b style={{color: deviceWriteEnabled ? '#389e0d' : '#cf1322'}}>
                {deviceWriteEnabled ? '已开启' : '已关闭'}
              </b>
            </span>

            <span>任务领取：
              <b style={{color: taskPollingEnabled ? '#389e0d' : '#cf1322'}}>
                {taskPollingEnabled ? '已开启' : '已关闭'}
              </b>
            </span>
          </div>

          {testRoom && (
            <div style={s.tip}>🧪 当前测试房号：<b>{testRoom}</b></div>
          )}

          {!writeEnabled && (
            <div style={s.warn}>
              🔒 当前安全锁 write_enabled=0。可以查看数据，但点击下发时后端会拒绝写入安居宝。
            </div>
          )}

          {gateway.last_message && (
            <div style={s.message}>网关消息：{gateway.last_message}</div>
          )}
        </section>

        <section style={s.card}>
          <div style={s.sectionHead}>
            <div>
              <b>住户门禁人员</b>
              <div style={s.muted}>已采集人脸 {people.length} 人</div>
            </div>
          </div>

          <input
            style={s.search}
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            placeholder="搜索姓名、房号、手机号"
          />

          {loading ? (
            <div style={s.empty}>正在读取门禁资料...</div>
          ) : filtered.length === 0 ? (
            <div style={s.empty}>暂无符合条件的人员</div>
          ) : (
            <div>
              {filtered.map((p, index) => {
                const st = badge(p.status || p.access_status || p.sync_status);
                const uid = p.user_id || p.id || index;
                return (
                  <div style={s.person} key={`${uid}-${p.person_uuid || index}`}>
                    <div style={s.personTop}>
                      {p.face_photo ? (
                        <img style={s.avatar} src={imageUrl(p.face_photo)} alt="" />
                      ) : (
                        <div style={s.avatarEmpty}>👤</div>
                      )}

                      <div style={s.personMain}>
                        <div style={s.nameRow}>
                          <b style={s.name}>{p.real_name || '未命名'}</b>
                          <span style={{...s.status, background:st.bg, color:st.color}}>
                            {st.text}
                          </span>
                        </div>
                        <div style={s.room}>{p.room_no || '未填写房号'}</div>
                        {p.phone && <div style={s.muted}>{p.phone}</div>}
                        {p.last_result && <div style={s.result}>{p.last_result}</div>}
                      </div>
                    </div>

                    <button
                      style={{
                        ...s.syncBtn,
                        ...((busyId === (p.user_id || p.id) || !p.face_photo) ? s.disabled : {})
                      }}
                      disabled={busyId === (p.user_id || p.id) || !p.face_photo}
                      onClick={() => syncPerson(p)}
                    >
                      {busyId === (p.user_id || p.id) ? '正在下发...' :
                        p.face_photo ? '下发到安居宝' : '缺少人脸照片'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section style={s.card}>
          <div style={s.sectionHead}>
            <div>
              <b>最近门禁任务</b>
              <div style={s.muted}>显示最近 {Math.min(tasks.length, 30)} 条</div>
            </div>
          </div>

          {tasks.length === 0 ? (
            <div style={s.empty}>暂无门禁任务记录</div>
          ) : (
            tasks.slice(0, 30).map((t, i) => {
              const st = badge(t.status);
              return (
                <div style={s.task} key={t.task_uuid || t.id || i}>
                  <div style={s.taskLine}>
                    <b>{t.real_name || t.room_no || `任务 #${t.id || i + 1}`}</b>
                    <span style={{...s.status, background:st.bg, color:st.color}}>{st.text}</span>
                  </div>
                  <div style={s.muted}>
                    {t.room_no || '—'} · {t.action || 'register'} · {fmtTime(t.created_at)}
                  </div>
                  {t.result_message && <div style={s.result}>{t.result_message}</div>}
                </div>
              );
            })
          )}
        </section>

        <div style={s.footer}>
          页面每15秒自动刷新 · 网关心跳正常后会自动领取 pending 任务
        </div>
      </main>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div style={s.info}>
      <div style={s.infoLabel}>{label}</div>
      <div style={s.infoValue}>{value}</div>
    </div>
  );
}

const s = {
  page: { minHeight:'100vh', background:'#f4f6f9', paddingBottom:80 },
  header: {
    background:'linear-gradient(135deg,#174b86,#2f86db)',
    color:'#fff', padding:'18px 14px', display:'grid',
    gridTemplateColumns:'76px 1fr 58px', alignItems:'center', gap:8,
    position:'sticky', top:0, zIndex:20, boxShadow:'0 2px 10px rgba(0,0,0,.12)'
  },
  back: { border:0, background:'transparent', color:'#fff', fontSize:16, textAlign:'left', padding:4 },
  title: { fontSize:22, fontWeight:800, textAlign:'center' },
  subTitle: { fontSize:11, opacity:.85, textAlign:'center', marginTop:3 },
  refresh: { border:'1px solid rgba(255,255,255,.45)', background:'rgba(255,255,255,.14)', color:'#fff', borderRadius:8, padding:'7px 6px' },
  body: { padding:'14px 12px 24px' },
  card: { background:'#fff', borderRadius:16, padding:15, marginBottom:13, boxShadow:'0 3px 14px rgba(0,0,0,.06)' },
  sectionHead: { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12, fontSize:17 },
  onlineBadge: { fontSize:13, fontWeight:700, padding:'5px 10px', borderRadius:20 },
  online: { color:'#237804', background:'#f6ffed' },
  offline: { color:'#cf1322', background:'#fff1f0' },
  gatewayName: { fontSize:15, fontWeight:700, marginBottom:12, color:'#333' },
  grid: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:9 },
  info: { background:'#f7f9fc', borderRadius:11, padding:'10px 11px', minWidth:0 },
  infoLabel: { color:'#888', fontSize:12, marginBottom:4 },
  infoValue: { color:'#222', fontSize:14, fontWeight:700, wordBreak:'break-all' },
  modeRow: { display:'flex', justifyContent:'space-between', gap:8, flexWrap:'wrap', marginTop:12, fontSize:13, color:'#555' },
  tip: { marginTop:10, padding:'9px 10px', borderRadius:9, background:'#f0f5ff', color:'#1d39c4', fontSize:13 },
  warn: { marginTop:10, padding:'10px 11px', borderRadius:9, background:'#fff7e6', color:'#ad4e00', lineHeight:1.55, fontSize:13 },
  message: { marginTop:9, color:'#777', fontSize:12, wordBreak:'break-all' },
  error: { background:'#fff1f0', color:'#cf1322', borderRadius:10, padding:'11px 12px', marginBottom:12 },
  muted: { color:'#999', fontSize:12, marginTop:3 },
  search: { width:'100%', boxSizing:'border-box', border:'1px solid #e1e5ea', background:'#fafafa', borderRadius:10, padding:'11px 12px', fontSize:15, outline:'none', marginBottom:8 },
  person: { padding:'13px 0', borderBottom:'1px solid #f0f0f0' },
  personTop: { display:'flex', gap:12, alignItems:'flex-start' },
  avatar: { width:64, height:64, objectFit:'cover', borderRadius:12, background:'#eee', flex:'0 0 64px' },
  avatarEmpty: { width:64, height:64, borderRadius:12, background:'#f3f3f3', display:'flex', alignItems:'center', justifyContent:'center', fontSize:28, flex:'0 0 64px' },
  personMain: { flex:1, minWidth:0 },
  nameRow: { display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 },
  name: { fontSize:17, color:'#222' },
  room: { fontSize:14, color:'#555', marginTop:5 },
  status: { padding:'3px 8px', borderRadius:20, fontSize:11, fontWeight:700, whiteSpace:'nowrap' },
  result: { marginTop:6, fontSize:12, color:'#666', background:'#fafafa', padding:'6px 8px', borderRadius:7, wordBreak:'break-all' },
  syncBtn: { width:'100%', marginTop:10, border:0, borderRadius:10, padding:'10px 12px', background:'#1677ff', color:'#fff', fontSize:14, fontWeight:700 },
  disabled: { background:'#bfbfbf' },
  task: { padding:'11px 0', borderBottom:'1px solid #f0f0f0' },
  taskLine: { display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, fontSize:14 },
  empty: { textAlign:'center', color:'#999', padding:'28px 0' },
  footer: { textAlign:'center', color:'#aaa', fontSize:11, padding:'4px 12px 18px', lineHeight:1.6 }
};
